/** @jsx jsx */
import { css, jsx } from "@emotion/react"
import React, { useEffect, useRef, useState } from "react"
import { classNames } from "../../../../utils"
import ErrorMessage from "../ErrorMessage"
import ListString from "./ListString"

const fontStyles = `font-family: "Jost";
     font-style: normal;
     font-weight: 400;
     font-size: 15px;
     line-height: 28px;
     letter-spacing: 0.02em;
     color: #717171;`

const inputClassName =
  "block py-[16px] px-[18px] w-full bg-transparent border border-[#EBEBEB] rounded-[8px] appearance-none focus:text-[#222] focus:outline-none focus:ring-0 focus:border-[#222] peer"

const labelClassName =
  "absolute duration-300 transform -translate-y-7 scale-75 top-[15px] left-[12px] z-10 origin-[0] px-[8px] peer-placeholder-shown:scale-100 bg-white peer-placeholder-shown:-z-10 peer-placeholder-shown:bg-transparent peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-7 bg-white peer-focus:bg-white peer-focus:z-10"

// Accept only an optional decimal number while the user types.
const DECIMAL_PATTERN = /^\d*\.?\d*$/

// Composite control for the product THC/CBD content: a fixed amount by
// default, or a min/max range when the range checkbox is on, plus the
// required mg/g unit select. The existing amount field acts as the range
// minimum; an empty maximum means fixed-amount mode (issue #375).
const ThcCbdRange = ({
  data,
  maxData,
  unitData,
  errors,
  name,
  maxName,
  unitName,
  register,
  setValue,
  watch,
  control,
}) => {
  // The schema label bakes in "(mg)", which contradicts the unit select
  // once grams can be chosen, so the unit lives only in the dropdown.
  const baseLabel = (data.label || "").replace(/\s*\(mg\)\s*$/i, "")

  const [isRange, setIsRange] = useState(
    Boolean(maxData.current_value && maxData.current_value !== "")
  )
  const [amount, setAmount] = useState(data.current_value || "")
  const [maxVal, setMaxVal] = useState(maxData.current_value || "")

  // The max validator must see the live checkbox state even after the max
  // input unmounts, because react-hook-form keeps the field registered.
  const isRangeRef = useRef(isRange)
  useEffect(() => {
    isRangeRef.current = isRange
  }, [isRange])

  const selectedUnit = watch(unitName)

  useEffect(() => {
    // In range mode the base field is the required minimum; validate reads
    // the live form values RHF passes in rather than a stale watch closure.
    register(name, {
      validate: (value, formValues) => {
        if (!isRangeRef.current) return true
        if (value === "" || value === null || value === undefined) {
          return `Enter a ${baseLabel} minimum or uncheck "${baseLabel} range".`
        }
        const min = parseFloat(value)
        if (isNaN(min) || min < 0) {
          return `${baseLabel} must be a non-negative number.`
        }
        return true
      },
    })
    register(maxName, {
      validate: (value, formValues) => {
        if (!isRangeRef.current) return true
        if (value === "" || value === null || value === undefined) {
          return `Enter a maximum value or uncheck "${baseLabel} range".`
        }
        const max = parseFloat(value)
        if (isNaN(max) || max < 0) {
          return "Maximum must be a non-negative number."
        }
        const min = parseFloat(formValues[name])
        // Match the backend guard, which rejects only max < min (equal
        // bounds are legal), so an existing "25-25" range can be re-saved.
        if (!isNaN(min) && max < min) {
          return `Maximum cannot be less than the ${baseLabel} value.`
        }
        return true
      },
    })
    // A legacy product can come back with an empty unit; the backend
    // treats "" as mg, so preselect the default to keep the visible
    // selection and the submitted value in sync (issue #375).
    if (!watch(unitName)) {
      setValue(unitName, unitData.default_value || "mg")
    }
  }, [])

  const toggleRange = checked => {
    // Update the ref synchronously so the validators (which run before the
    // syncing effect commits) already see the new mode when the max clears.
    isRangeRef.current = checked
    setIsRange(checked)
    if (!checked) {
      // Unchecking the box returns the product to fixed-amount mode and
      // revalidates both fields, so a stale "enter a minimum/maximum" error
      // is dropped instead of resurfacing the instant range mode is
      // re-checked.
      setMaxVal("")
      setValue(maxName, "", { shouldValidate: true, shouldDirty: true })
      setValue(name, amount, { shouldValidate: true })
    }
    // Do not validate when range turns on. Empty min/max should only
    // error after Save (issue #373 QA).
  }

  const handleDecimalChange = (value, setState, fieldName) => {
    if (DECIMAL_PATTERN.test(value)) {
      setState(value)
      // Revalidate while typing only after a failed Save, so ticking
      // the range checkbox does not show a red error immediately.
      const rangeHasError = Boolean(
        (errors && errors[name]) || (errors && errors[maxName])
      )
      setValue(fieldName, value, {
        shouldDirty: true,
        shouldValidate: rangeHasError,
      })
      if (fieldName === name && rangeHasError) {
        setValue(maxName, watch(maxName), { shouldValidate: true })
      }
    }
  }

  return (
    <div className="relative mb-6 w-full group">
      <div className="flex items-center mb-[12px]">
        <input
          id={`${name}_range`}
          type="checkbox"
          checked={isRange}
          onChange={e => toggleRange(e.target.checked)}
          className="h-4 w-4 border-[#EBEBEB] rounded rounded-[4px] text-[#222] focus:ring-1 focus:ring-offset-2 focus:ring-[#222]"
        />
        <label
          htmlFor={`${name}_range`}
          className="ml-3 text-[#222]"
          css={css`
            ${fontStyles}
          `}
        >
          {baseLabel} range
        </label>
      </div>
      <div
        className={classNames(
          "grid md:gap-[15px]",
          isRange ? "md:grid-cols-3" : "md:grid-cols-2"
        )}
      >
        <div className="relative z-0 w-full group">
          <input
            type="text"
            inputMode="decimal"
            value={amount}
            onChange={e =>
              handleDecimalChange(e.target.value, setAmount, name)
            }
            className={classNames(
              inputClassName,
              isRange &&
                errors[name] &&
                "border-red-600 focus:border-red-600"
            )}
            css={css`
              border-radius: 8px;
              ${fontStyles}
            `}
            placeholder=" "
          />
          <span
            className="absolute top-[16px] right-[18px] pointer-events-none"
            css={css`
              ${fontStyles}
            `}
          >
            {selectedUnit || unitData.default_value || "mg"}
          </span>
          <label
            htmlFor={name}
            className={labelClassName}
            css={css`
              ${fontStyles}
            `}
          >
            {isRange ? `${baseLabel} (min)` : baseLabel}
          </label>
        </div>
        {isRange && (
          <div className="relative z-0 w-full group">
            <input
              type="text"
              inputMode="decimal"
              value={maxVal}
              onChange={e =>
                handleDecimalChange(e.target.value, setMaxVal, maxName)
              }
              className={classNames(
                inputClassName,
                errors[maxName] && "border-red-600 focus:border-red-600"
              )}
              css={css`
                border-radius: 8px;
                ${fontStyles}
              `}
              placeholder=" "
            />
            <label
              htmlFor={maxName}
              className={labelClassName}
              css={css`
                ${fontStyles}
              `}
            >
              {`${baseLabel} (max)`}
            </label>
          </div>
        )}
        <ListString
          data={unitData}
          errors={errors}
          name={unitName}
          register={register}
          label={
            unitData.required ? `${unitData.label} *` : unitData.label
          }
          setValue={setValue}
          watch={watch}
          control={control}
          menuPortal
        />
      </div>
      {isRange && errors && errors[name] && (
        <ErrorMessage message={errors[name]["message"]} />
      )}
      {isRange && errors && errors[maxName] && (
        <ErrorMessage message={errors[maxName]["message"]} />
      )}
      <span
        css={css`
          font-family: "Jost";
          font-style: normal;
          font-weight: 400;
          font-size: 12px;
          line-height: 16px;
          display: inline-block;
          padding: 0 10px;
          margin: 7px 0 10px;
          letter-spacing: 0.02em;
          color: #717171;
        `}
      >
        {isRange ? maxData.description : data.description}
      </span>
    </div>
  )
}

export default ThcCbdRange
